import React, { useCallback, useState } from 'react';

import styled from '@emotion/styled';

import { NavLinkButton, Button, Modal, Table } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';
import { useActiveStorageDirectUpload } from '@shared/hooks/use_active_storage_direct_upload';
import { UserRole } from '@shared/types/user_role';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { ROLES } from '@admin/config/roles';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  SopDocumentKindEnum,
  SopDocumentsQuery,
  useSopDocumentsQuery,
  useSopDocumentUpsertMutation,
} from '@admin/schema';

type SopDocumentRow = NonNullable<SopDocumentsQuery['sopDocuments']>[number];
type SopDocumentKindInfo = NonNullable<SopDocumentsQuery['sopDocumentKinds']>[number];
type VersionHistoryRow = SopDocumentRow['versionHistory'][number];

const ToggleCell = styled.td`
  text-align: center;
  vertical-align: middle !important;
`;

const ToggleCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const VersionHistoryButton = styled.button`
  background: none;
  border: none;
  color: teal;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: #005a5a;
  }
`;

const ActionCell = styled.td`
  white-space: nowrap;
  vertical-align: middle !important;
`;

const FileLink = styled(NavLinkButton)`
  color: teal;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ active }) => (active ? '#dff0d8' : '#f2dede')};
  color: ${({ active }) => (active ? '#3c763d' : '#a94442')};
`;

const KindLabel = styled.code`
  font-size: 11px;
  color: #777;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  margin: 1px 2px;
  border-radius: 3px;
  font-size: 11px;
  background-color: #e8f5f0;
  color: #2c7a5e;
  white-space: nowrap;
`;

const EditableNameInput = styled.input`
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 13px;
  width: 100%;
  max-width: 200px;

  &:focus {
    border-color: teal;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 128, 128, 0.15);
  }
`;

interface FileUploadRowProps {
  kind: string;
  onUploaded: (signedID: string) => void;
  onCancel: () => void;
}

const FileUploadRow: React.FC<FileUploadRowProps> = ({ kind, onUploaded, onCancel }) => {
  const [file, setFile] = useState<File | null>(null);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="file"
        accept="application/pdf"
        aria-label="Upload SOP PDF"
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (selected) setFile(selected);
        }}
      />
      {file && <FileUploader file={file} onUploaded={onUploaded} onCancel={onCancel} />}
      {!file && (
        <Button kind="default" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
};

interface FileUploaderProps {
  file: File;
  onUploaded: (signedID: string) => void;
  onCancel: () => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ file, onUploaded, onCancel }) => {
  const { uploading, loaded, total } = useActiveStorageDirectUpload({
    file,
    onUpload: (blob) => onUploaded(blob.signed_id),
    onError: () => onCancel(),
  });

  if (uploading) {
    const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
    return <span>Uploading... {percent}%</span>;
  }

  return null;
};

interface VersionHistoryModalProps {
  sop: SopDocumentRow;
  onClose: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({ sop, onClose }) => (
  <Modal onClose={onClose} size={Modal.Size.Large}>
    <Modal.Content>
      <Modal.Header>
        <Modal.Close close={onClose} />
        <Modal.Title>Version History - {sop.displayName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {sop.versionHistory.length === 0 ? (
          <p className="text-muted">No previous versions found.</p>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th>File</th>
                <th>Uploaded At</th>
                <th>Replaced At</th>
              </tr>
            </thead>
            <tbody>
              {sop.versionHistory.map((version: VersionHistoryRow) => (
                <tr key={version.id}>
                  <td>
                    {version.file ? (
                      <FileLink to={version.file.url} target="_blank">
                        {version.file.filename}
                      </FileLink>
                    ) : (
                      <span className="text-muted">No file</span>
                    )}
                  </td>
                  <td>
                    <Timestamp value={version.createdAt} format={Timestamp.Format.DateTime} />
                  </td>
                  <td>
                    {version.deletedAt && <Timestamp value={version.deletedAt} format={Timestamp.Format.DateTime} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button kind="default" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal.Content>
  </Modal>
);

export const SopDocumentList: React.FC = () => {
  useTitle('SOP Documents');
  const canEditSops = ROLES.has(UserRole.Admin);
  const { data, loading, error, refetch } = useSopDocumentsQuery({
    client,
    fetchPolicy: 'network-only',
  });
  const [upsertMutation] = useSopDocumentUpsertMutation({ client });
  const [uploadingKind, setUploadingKind] = useState<SopDocumentKindEnum | null>(null);
  const [versionHistorySop, setVersionHistorySop] = useState<SopDocumentRow | null>(null);
  const [savingToggles, setSavingToggles] = useState<Set<SopDocumentKindEnum>>(new Set());
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<{ kind: SopDocumentKindEnum; value: string } | null>(null);

  const sopDocuments = data?.sopDocuments ?? [];
  const kindInfos = [...(data?.sopDocumentKinds ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName));

  const existingByKind = new Map<SopDocumentKindEnum, SopDocumentRow>();
  for (const sop of sopDocuments) {
    existingByKind.set(sop.kind, sop);
  }

  const handleToggle = useCallback(
    async (kind: SopDocumentKindEnum, field: 'dockEnabled' | 'fieldEnabled' | 'forkliftEnabled', value: boolean) => {
      setSavingToggles((prev) => new Set(prev).add(kind));
      setMutationError(null);
      try {
        const { data: result } = await upsertMutation({
          variables: {
            input: {
              kind,
              [field]: value,
            },
          },
        });
        if (result?.sopDocumentUpsert?.error) {
          setMutationError(result.sopDocumentUpsert.error);
          return;
        }
        await refetch();
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to update toggle');
      } finally {
        setSavingToggles((prev) => {
          const next = new Set(prev);
          next.delete(kind);
          return next;
        });
      }
    },
    [upsertMutation, refetch],
  );

  const handleReplacePDF = useCallback(
    async (kind: SopDocumentKindEnum, signedID: string) => {
      setUploadingKind(null);
      setMutationError(null);
      try {
        const { data: result } = await upsertMutation({
          variables: {
            input: {
              kind,
              fileSignedID: signedID,
            },
          },
        });
        if (result?.sopDocumentUpsert?.error) {
          setMutationError(result.sopDocumentUpsert.error);
          return;
        }
        await refetch();
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to replace PDF');
      }
    },
    [upsertMutation, refetch],
  );

  const handleSaveDisplayName = useCallback(
    async (kind: SopDocumentKindEnum, displayName: string) => {
      setEditingName(null);
      setMutationError(null);
      try {
        const { data: result } = await upsertMutation({
          variables: {
            input: { kind, displayName },
          },
        });
        if (result?.sopDocumentUpsert?.error) {
          setMutationError(result.sopDocumentUpsert.error);
          return;
        }
        await refetch();
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to update display name');
      }
    },
    [upsertMutation, refetch],
  );

  return (
    <>
      <Panel>
        <Panel.Header>
          <Panel.Title>SOP Documents</Panel.Title>
        </Panel.Header>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Kind</th>
              <th>Display Name</th>
              <th>Current PDF</th>
              <th>Linked Categories</th>
              <th style={{ textAlign: 'center' }}>Field</th>
              <th style={{ textAlign: 'center' }}>Dock</th>
              <th style={{ textAlign: 'center' }}>Forklift</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {kindInfos.map((kindInfo: SopDocumentKindInfo) => {
              const { kind } = kindInfo;
              const sop = existingByKind.get(kind);
              const displayName = sop?.displayName ?? kindInfo.displayName;
              const isSaving = savingToggles.has(kind);
              const isEditingThis = editingName?.kind === kind;

              return (
                <tr key={kind}>
                  <td style={{ verticalAlign: 'middle' }}>
                    <KindLabel>{kind}</KindLabel>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {canEditSops && isEditingThis ? (
                      <EditableNameInput
                        type="text"
                        defaultValue={editingName.value}
                        aria-label={`Edit display name for ${kind}`}
                        autoFocus
                        onBlur={(e) => {
                          const trimmed = e.target.value.trim();
                          if (trimmed && trimmed !== displayName && sop) {
                            void handleSaveDisplayName(kind, trimmed);
                          } else {
                            setEditingName(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          } else if (e.key === 'Escape') {
                            setEditingName(null);
                          }
                        }}
                      />
                    ) : (
                      <span
                        style={{ cursor: canEditSops && sop ? 'pointer' : 'default' }}
                        title={canEditSops ? (sop ? 'Click to edit' : 'Upload a PDF first') : undefined}
                        onClick={() => canEditSops && sop && setEditingName({ kind, value: displayName })}
                      >
                        <strong>{displayName}</strong>
                      </span>
                    )}
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {canEditSops && uploadingKind === kind ? (
                      <FileUploadRow
                        kind={kind}
                        onUploaded={(signedID) => void handleReplacePDF(kind, signedID)}
                        onCancel={() => setUploadingKind(null)}
                      />
                    ) : sop?.file ? (
                      <FileLink to={sop.file.url} target="_blank">
                        {sop.file.filename}
                      </FileLink>
                    ) : (
                      <StatusBadge active={false}>No PDF</StatusBadge>
                    )}
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {kindInfo.linkedCategories.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {kindInfo.linkedCategories.map((cat) => (
                          <CategoryBadge key={cat}>{cat}</CategoryBadge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        None
                      </span>
                    )}
                  </td>
                  <ToggleCell>
                    {canEditSops ? (
                      <ToggleCheckbox
                        type="checkbox"
                        checked={sop?.fieldEnabled ?? false}
                        disabled={!sop || isSaving}
                        aria-label={`Field toggle for ${displayName}`}
                        onChange={(e) => void handleToggle(kind, 'fieldEnabled', e.target.checked)}
                      />
                    ) : (
                      <span>{sop?.fieldEnabled ? 'Yes' : 'No'}</span>
                    )}
                  </ToggleCell>
                  <ToggleCell>
                    {canEditSops ? (
                      <ToggleCheckbox
                        type="checkbox"
                        checked={sop?.dockEnabled ?? false}
                        disabled={!sop || isSaving}
                        aria-label={`Dock toggle for ${displayName}`}
                        onChange={(e) => void handleToggle(kind, 'dockEnabled', e.target.checked)}
                      />
                    ) : (
                      <span>{sop?.dockEnabled ? 'Yes' : 'No'}</span>
                    )}
                  </ToggleCell>
                  <ToggleCell>
                    {canEditSops ? (
                      <ToggleCheckbox
                        type="checkbox"
                        checked={sop?.forkliftEnabled ?? false}
                        disabled={!sop || isSaving}
                        aria-label={`Forklift toggle for ${displayName}`}
                        onChange={(e) => void handleToggle(kind, 'forkliftEnabled', e.target.checked)}
                      />
                    ) : (
                      <span>{sop?.forkliftEnabled ? 'Yes' : 'No'}</span>
                    )}
                  </ToggleCell>
                  <td style={{ verticalAlign: 'middle' }}>
                    {sop?.updatedAt && <Timestamp value={sop.updatedAt} format={Timestamp.Format.DateTime} />}
                  </td>
                  <ActionCell>
                    {canEditSops && (
                      <Button kind="warning" onClick={() => setUploadingKind(kind)} disabled={uploadingKind === kind}>
                        {sop ? 'Replace PDF' : 'Upload PDF'}
                      </Button>
                    )}
                    {sop && (
                      <div style={{ marginTop: canEditSops ? 4 : 0 }}>
                        <VersionHistoryButton onClick={() => setVersionHistorySop(sop)}>
                          View Version History
                        </VersionHistoryButton>
                      </div>
                    )}
                  </ActionCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {loading && <Spinner />}
        {mutationError && (
          <div className="alert alert-danger" role="alert">
            {mutationError}
          </div>
        )}
        {error && (
          <div className="alert alert-danger" role="alert">
            Failed to load SOP documents
          </div>
        )}
      </Panel>
      {versionHistorySop && <VersionHistoryModal sop={versionHistorySop} onClose={() => setVersionHistorySop(null)} />}
    </>
  );
};
